import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { InfoIcon, AlertTriangleIcon } from 'lucide-react';
type RepaymentType = 'PI' | 'IO';
type LenderTier = 'tier1' | 'tier2' | 'tier3';
export function LoanCalculator() {
  const [principal, setPrincipal] = useState<number>(500000);
  const [rate, setRate] = useState<number>(5.5);
  const [years, setYears] = useState<number>(30);
  const [repaymentType, setRepaymentType] = useState<RepaymentType>('PI');
  const [monthlyFee, setMonthlyFee] = useState<number>(10);
  const [annualFee, setAnnualFee] = useState<number>(395);
  // Borrowing Capacity State
  const [income, setIncome] = useState<number>(120000);
  const [expenses, setExpenses] = useState<number>(4000);
  const [selectedTier, setSelectedTier] = useState<LenderTier>('tier1');
  const loanResults = useMemo(() => {
    const p = principal || 0;
    const r = (rate || 0) / 100 / 12;
    const n = (years || 0) * 12;
    const mFee = monthlyFee || 0;
    const aFee = annualFee || 0;
    if (p === 0 || r === 0 || n === 0) {
      return {
        monthlyPayment: 0,
        totalInterest: 0,
        totalCost: 0,
        totalFees: 0,
        chartData: []
      };
    }
    let monthlyPayment = 0;
    let totalInterest = 0;
    let totalCost = 0;
    const totalFees = mFee * n + aFee * (years || 0);
    if (repaymentType === 'PI') {
      monthlyPayment = p * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
      totalCost = monthlyPayment * n + totalFees;
      totalInterest = monthlyPayment * n - p;
    } else {
      // Interest Only (assuming IO for the full term for simplicity, though usually it's 1-5 years)
      monthlyPayment = p * r;
      totalInterest = monthlyPayment * n;
      totalCost = p + totalInterest + totalFees;
    }
    // Generate chart data (yearly)
    const chartData = [];
    let balance = p;
    let cumulativeInterest = 0;
    for (let year = 0; year <= years; year++) {
      if (year === 0) {
        chartData.push({
          year,
          balance: Math.round(balance),
          interest: 0
        });
        continue;
      }
      for (let month = 1; month <= 12; month++) {
        const interestPayment = balance * r;
        let principalPayment = 0;
        if (repaymentType === 'PI') {
          principalPayment = monthlyPayment - interestPayment;
        }
        balance -= principalPayment;
        cumulativeInterest += interestPayment;
      }
      chartData.push({
        year,
        balance: Math.max(0, Math.round(balance)),
        interest: Math.round(cumulativeInterest)
      });
    }
    return {
      monthlyPayment: monthlyPayment + mFee + aFee / 12,
      totalInterest,
      totalFees,
      totalCost,
      chartData
    };
  }, [principal, rate, years, repaymentType, monthlyFee, annualFee]);
  // Borrowing Capacity Estimation Logic
  // This is highly simplified and for illustrative purposes only
  const capacityResults = useMemo(() => {
    const annualIncome = income || 0;
    const monthlyExpenses = expenses || 0;
    // Base capacity multiplier (very simplified DTI approach)
    // Tier 1: Stricter assessment (HEM + Buffer + Lower DTI)
    // Tier 2: More flexible (Actuals + Buffer + Higher DTI) - Biggest jump
    // Tier 3: Specialist (Flexible income + Highest DTI) - Smaller jump from Tier 2
    const baseCapacity = Math.max(0, (annualIncome - monthlyExpenses * 12) * 5);
    const tier1Capacity = baseCapacity * 1.0; // Baseline
    const tier2Capacity = baseCapacity * 1.25; // ~25% jump from Tier 1
    const tier3Capacity = baseCapacity * 1.35; // ~10% jump from Tier 2
    return {
      tier1: Math.round(tier1Capacity),
      tier2: Math.round(tier2Capacity),
      tier3: Math.round(tier3Capacity)
    };
  }, [income, expenses]);
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  }).format(val);
  return <div className="space-y-12" data-id="element-789">
      {/* SECTION 1: Loan Repayments */}
      <div data-id="element-790">
        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6" data-id="element-791">
          Loan Repayment Calculator
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-792">
          {/* Inputs */}
          <div className="lg:col-span-1 space-y-5 bg-slate-50 dark:bg-white/5 p-5 sm:p-6 rounded-2xl border border-slate-200 dark:border-white/10" data-id="element-793">
            <div className="flex bg-slate-200 dark:bg-[#0f1629] p-1 rounded-lg mb-2" data-id="element-794">
              <button onClick={() => setRepaymentType('PI')} className={`flex-1 py-2 text-xs sm:text-sm font-medium rounded-md transition-all ${repaymentType === 'PI' ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`} data-id="element-795">
                Principal & Interest
              </button>
              <button onClick={() => setRepaymentType('IO')} className={`flex-1 py-2 text-xs sm:text-sm font-medium rounded-md transition-all ${repaymentType === 'IO' ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`} data-id="element-796">
                Interest Only
              </button>
            </div>

            <div data-id="element-797">
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-798">
                Loan Amount ($)
              </label>
              <input type="number" value={principal} onChange={e => setPrincipal(Number(e.target.value))} className="w-full bg-white dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all" data-id="element-799" />
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-800">
              <div data-id="element-801">
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-802">
                  Interest Rate (%)
                </label>
                <input type="number" step="0.1" value={rate} onChange={e => setRate(Number(e.target.value))} className="w-full bg-white dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all" data-id="element-803" />
              </div>
              <div data-id="element-804">
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-805">
                  Term (Years)
                </label>
                <input type="number" value={years} onChange={e => setYears(Number(e.target.value))} className="w-full bg-white dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all" data-id="element-806" />
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 dark:border-white/10" data-id="element-807">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3" data-id="element-808">
                Ongoing Fees
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-809">
                <div data-id="element-810">
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-811">
                    Monthly Fee ($)
                  </label>
                  <input type="number" value={monthlyFee} onChange={e => setMonthlyFee(Number(e.target.value))} className="w-full bg-white dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all" data-id="element-812" />
                </div>
                <div data-id="element-813">
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-814">
                    Annual Fee ($)
                  </label>
                  <input type="number" value={annualFee} onChange={e => setAnnualFee(Number(e.target.value))} className="w-full bg-white dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all" data-id="element-815" />
                </div>
              </div>
            </div>
          </div>

          {/* Outputs & Chart */}
          <div className="lg:col-span-2 space-y-6" data-id="element-816">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-817">
              <div className="bg-slate-50 dark:bg-white/5 p-5 rounded-2xl border border-slate-200 dark:border-white/10" data-id="element-818">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1" data-id="element-819">
                  Monthly Repayment
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-primary-500 dark:text-primary-400" data-id="element-820">
                  {formatCurrency(loanResults.monthlyPayment)}
                </p>
                <p className="text-xs text-slate-400 mt-1" data-id="element-821">Includes fees</p>
              </div>
              <div className="bg-slate-50 dark:bg-white/5 p-5 rounded-2xl border border-slate-200 dark:border-white/10" data-id="element-822">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1" data-id="element-823">
                  Total Interest
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-accent-500 dark:text-accent-400" data-id="element-824">
                  {formatCurrency(loanResults.totalInterest)}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-white/5 p-5 rounded-2xl border border-slate-200 dark:border-white/10" data-id="element-825">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1" data-id="element-826">
                  Total Cost
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white" data-id="element-827">
                  {formatCurrency(loanResults.totalCost)}
                </p>
                <p className="text-xs text-slate-400 mt-1" data-id="element-828">
                  Principal + Interest + Fees
                </p>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-white/5 p-4 sm:p-6 rounded-2xl border border-slate-200 dark:border-white/10 h-[300px] sm:h-[400px]" data-id="element-829">
              <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-6" data-id="element-830">
                Amortization Schedule
              </h4>
              <ResponsiveContainer width="100%" height="100%" data-id="element-831">
                <AreaChart data={loanResults.chartData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-832">
                  <defs data-id="element-833">
                    <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1" data-id="element-834">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} data-id="element-835" />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-836" />
                    </linearGradient>
                    <linearGradient id="colorInterest" x1="0" y1="0" x2="0" y2="1" data-id="element-837">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} data-id="element-838" />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} data-id="element-839" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-840" />
                  <XAxis dataKey="year" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickFormatter={val => `Yr ${val}`} data-id="element-841" />
                  <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickFormatter={val => `$${val / 1000}k`} data-id="element-842" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#0f172a',
                  borderColor: '#1e293b',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px'
                }} formatter={(value: number) => formatCurrency(value)} labelFormatter={label => `Year ${label}`} data-id="element-843" />
                  <Area type="monotone" dataKey="balance" name="Remaining Balance" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorBalance)" data-id="element-844" />
                  <Area type="monotone" dataKey="interest" name="Cumulative Interest" stroke="#06b6d4" strokeWidth={2} fillOpacity={1} fill="url(#colorInterest)" data-id="element-845" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      <hr className="border-slate-200 dark:border-white/10" data-id="element-846" />

      {/* SECTION 2: Borrowing Capacity Estimator */}
      <div data-id="element-847">
        <div className="mb-6" data-id="element-848">
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2" data-id="element-849">
            Borrowing Capacity Estimator
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm max-w-3xl" data-id="element-850">
            Different lenders assess your capacity differently. See how moving
            between lender tiers can impact how much you can borrow.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-851">
          {/* Inputs */}
          <div className="lg:col-span-1 space-y-5 bg-slate-50 dark:bg-white/5 p-5 sm:p-6 rounded-2xl border border-slate-200 dark:border-white/10" data-id="element-852">
            <div data-id="element-853">
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-854">
                Gross Annual Income ($)
              </label>
              <input type="number" value={income} onChange={e => setIncome(Number(e.target.value))} className="w-full bg-white dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all" data-id="element-855" />
            </div>
            <div data-id="element-856">
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-857">
                Monthly Living Expenses ($)
              </label>
              <input type="number" value={expenses} onChange={e => setExpenses(Number(e.target.value))} className="w-full bg-white dark:bg-[#0f1629] border border-slate-200 dark:border-white/10 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all" data-id="element-858" />
            </div>

            <div className="mt-6 p-4 bg-primary-50 dark:bg-blue-900/20 border border-primary-100 dark:border-blue-500/20 rounded-xl" data-id="element-859">
              <div className="flex items-start gap-2" data-id="element-860">
                <InfoIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" data-id="element-861" />
                <p className="text-xs text-primary-800 dark:text-blue-200/80 leading-relaxed" data-id="element-862">
                  This is a highly simplified estimate. Actual capacity depends
                  on existing debts, dependents, credit cards, and specific
                  lender policies.
                </p>
              </div>
            </div>
          </div>

          {/* Tier Comparison */}
          <div className="lg:col-span-2" data-id="element-863">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-id="element-864">
              {/* Tier 1 */}
              <div onClick={() => setSelectedTier('tier1')} className={`cursor-pointer p-5 rounded-2xl border transition-all ${selectedTier === 'tier1' ? 'bg-white dark:bg-[#1a2035] border-primary-500 shadow-md' : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 hover:border-primary-300'}`} data-id="element-865">
                <div className="flex justify-between items-start mb-2" data-id="element-866">
                  <h4 className="font-semibold text-slate-900 dark:text-white" data-id="element-867">
                    Tier 1
                  </h4>
                  {selectedTier === 'tier1' && <span className="flex h-2.5 w-2.5 rounded-full bg-primary-500" data-id="element-868"></span>}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 h-8" data-id="element-869">
                  Major Banks (Stricter assessment)
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white" data-id="element-870">
                  {formatCurrency(capacityResults.tier1)}
                </p>
              </div>

              {/* Tier 2 */}
              <div onClick={() => setSelectedTier('tier2')} className={`cursor-pointer p-5 rounded-2xl border transition-all ${selectedTier === 'tier2' ? 'bg-white dark:bg-[#1a2035] border-accent-500 shadow-md' : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 hover:border-accent-300'}`} data-id="element-871">
                <div className="flex justify-between items-start mb-2" data-id="element-872">
                  <h4 className="font-semibold text-slate-900 dark:text-white" data-id="element-873">
                    Tier 2
                  </h4>
                  {selectedTier === 'tier2' && <span className="flex h-2.5 w-2.5 rounded-full bg-accent-500" data-id="element-874"></span>}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 h-8" data-id="element-875">
                  Mid-Tier Banks (More flexible)
                </p>
                <p className="text-2xl font-bold text-accent-600 dark:text-accent-400" data-id="element-876">
                  {formatCurrency(capacityResults.tier2)}
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 font-medium" data-id="element-877">
                  +
                  {formatCurrency(capacityResults.tier2 - capacityResults.tier1)}{' '}
                  vs Tier 1
                </p>
              </div>

              {/* Tier 3 */}
              <div onClick={() => setSelectedTier('tier3')} className={`cursor-pointer p-5 rounded-2xl border transition-all ${selectedTier === 'tier3' ? 'bg-white dark:bg-[#1a2035] border-purple-500 shadow-md' : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 hover:border-purple-300'}`} data-id="element-878">
                <div className="flex justify-between items-start mb-2" data-id="element-879">
                  <h4 className="font-semibold text-slate-900 dark:text-white" data-id="element-880">
                    Tier 3
                  </h4>
                  {selectedTier === 'tier3' && <span className="flex h-2.5 w-2.5 rounded-full bg-purple-500" data-id="element-881"></span>}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 h-8" data-id="element-882">
                  Specialist Lenders (Highest capacity)
                </p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400" data-id="element-883">
                  {formatCurrency(capacityResults.tier3)}
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 font-medium" data-id="element-884">
                  +
                  {formatCurrency(capacityResults.tier3 - capacityResults.tier2)}{' '}
                  vs Tier 2
                </p>
              </div>
            </div>

            {/* Tier Explanations */}
            <div className="bg-slate-50 dark:bg-white/5 p-5 sm:p-6 rounded-2xl border border-slate-200 dark:border-white/10" data-id="element-885">
              {selectedTier === 'tier1' && <div data-id="element-886">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-2" data-id="element-887">
                    Tier 1 Lenders (Major Banks)
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4" data-id="element-888">
                    Typically the "Big 4" banks. They offer highly competitive
                    rates but have the strictest assessment criteria.
                  </p>
                  <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2 list-disc pl-5" data-id="element-889">
                    <li data-id="element-890">
                      Use higher assessment buffers (often +3% above actual
                      rate)
                    </li>
                    <li data-id="element-891">
                      Strict Debt-to-Income (DTI) limits, usually capped around
                      6x
                    </li>
                    <li data-id="element-892">
                      Often use Household Expenditure Measure (HEM) benchmarks
                      which can reduce capacity
                    </li>
                    <li data-id="element-893">
                      Best for: Standard PAYG borrowers with low existing debt,
                      some structures are possible
                    </li>
                  </ul>
                </div>}
              {selectedTier === 'tier2' && <div data-id="element-894">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-2" data-id="element-895">
                    Tier 2 Lenders (Mid-Tier & Non-Majors)
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4" data-id="element-896">
                    Includes lenders like Macquarie, ING, Bankwest, etc. This is
                    where investors often see the{' '}
                    <strong className="text-slate-900 dark:text-white" data-id="element-897">
                      biggest jump in capacity
                    </strong>
                    .
                  </p>
                  <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2 list-disc pl-5" data-id="element-898">
                    <li data-id="element-899">
                      More generous assessment of rental income and existing
                      debts
                    </li>
                    <li data-id="element-900">Higher DTI allowances (often up to 7-8x)</li>
                    <li data-id="element-901">
                      May use actual living expenses rather than strict HEM
                      benchmarks
                    </li>
                    <li data-id="element-902">
                      Best for: Property investors hitting a wall with major
                      banks
                    </li>
                  </ul>
                </div>}
              {selectedTier === 'tier3' && <div data-id="element-903">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-2" data-id="element-904">
                    Tier 3 Lenders (Specialist/Non-Bank)
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4" data-id="element-905">
                    Includes lenders like Pepper, Liberty, La Trobe. They
                    provide a smaller incremental jump in capacity from Tier 2,
                    but at higher interest rates.
                  </p>
                  <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2 list-disc pl-5" data-id="element-906">
                    <li data-id="element-907">
                      Highly flexible income assessment (e.g., alt-doc for
                      self-employed)
                    </li>
                    <li data-id="element-908">Highest DTI allowances (sometimes 9-10x)</li>
                    <li data-id="element-909">
                      Rates are typically higher and may include risk fees
                    </li>
                    <li data-id="element-910">
                      Best for: Complex structures, self-employed, or maximizing
                      leverage
                    </li>
                  </ul>
                </div>}
            </div>

            <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400" data-id="element-911">
              <AlertTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-912" />
              <p data-id="element-913">
                <strong data-id="element-914">Disclaimer:</strong> This borrowing capacity estimator
                is for illustrative purposes only and does not constitute a
                quote or an offer of finance. Actual borrowing capacity is
                subject to full application, credit assessment, and specific
                lender policies which change frequently.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}